import React, { forwardRef, ForwardRefRenderFunction, useImperativeHandle, useState } from "react";

import { Grid, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as yup from "yup";

import Button, { Color } from "../../../../../components/Form/Button";
import Input from "../../../../../components/Form/Input";
import { PUBLIC_ROUTES } from "../../../../../constants";
import useYupValidation from "../../../../../hooks/useYupValidation";
import { validateForm } from "./actions";
import { EmailTitle } from "./styles";

interface ConfirmEmailAddressProps {
  email?: string;
  isLoading?: boolean;
  handleSubmit?: (e: React.FormEvent) => void;
}

export interface ConfirmEmailAddressHandle {
  password: string;
}

const ConfirmEmailAddressComponent: ForwardRefRenderFunction<
  ConfirmEmailAddressHandle,
  ConfirmEmailAddressProps
> = (props, ref) => {
  const { email, isLoading, handleSubmit } = props;

  const { t: translate } = useTranslation();
  const history = useHistory();

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const renderEmail = () => {
    if (email) {
      return (
        <Typography
          variant="body1"
          color="#465869"
          fontSize="24px"
          lineHeight="33px"
          fontWeight={700}
          fontFamily="Manrope"
          sx={{ wordBreak: "break-word" }}
        >
          {`${email} ${translate("isNowVerified")}`}
        </Typography>
      );
    }

    return null;
  };

  const handleSubmitForm = (event: React.FormEvent) => {
    event.preventDefault();
    if (validateForm({ password, confirmPassword }, translate, setErrors)) {
      if (handleSubmit) handleSubmit(event);
    }
  };

  useImperativeHandle(ref, () => ({
    password,
  }));

  return (
    <Stack
      direction="column"
      alignItems="flex-start"
      justifyContent="center"
      spacing={3}
      height="100%"
    >
      <form className="w-100" onSubmit={handleSubmitForm}>
        <Stack direction="column" spacing={3} width="100%">
          <EmailTitle data-cy="confirmedMessage">
            {renderEmail()}
            {translate("youJustNeedToCreateAPasswordForYourAccount")}
          </EmailTitle>

          <Grid item xs={12}>
            <Input
              required
              disabled={isLoading}
              label={translate("chooseAPassword")}
              placeholder={translate("useLettersNumbersAndSymbolsForSafety")}
              onChange={(event) => setPassword(event.target.value)}
              type="password"
              value={password}
              error={fieldHasError("password")}
              errorMessage={getErrorByField("password")}
              data-cy="password"
              inputProps={{
                autoComplete: "new-password",
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              required
              disabled={isLoading}
              label={translate("confirmYourPassword")}
              placeholder={translate("theSameOneFromAbove")}
              onChange={(event) => setConfirmPassword(event.target.value)}
              type="password"
              value={confirmPassword}
              error={fieldHasError("confirmPassword")}
              errorMessage={getErrorByField("confirmPassword")}
              data-cy="confirmPassword"
              inputProps={{
                autoComplete: "new-password",
              }}
            />
          </Grid>

          <Button
            type="submit"
            fullWidth
            customColor={Color.SECONDARY}
            loading={isLoading}
            data-cy="okButton"
            aria-label="submitButton"
          >
            {translate("create")}
          </Button>

          <Button
            fullWidth
            customColor={Color.DEFAULT}
            loading={isLoading}
            data-cy="goBackToLoginButton"
            aria-label="goBackToLoginButton"
            onClick={() => history.replace(PUBLIC_ROUTES.SIGN_IN)}
          >
            {translate("goBackToLogin")}
          </Button>
        </Stack>
      </form>
    </Stack>
  );
};

export default forwardRef(ConfirmEmailAddressComponent);
